// components/TimelineDetailsDrawer.jsx
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Divider,
  Chip,
  Stack,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Avatar,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Alert,
  ButtonGroup,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SoftTypography from "components/SoftTypography";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import WorkIcon from "@mui/icons-material/Work";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import WeekendIcon from "@mui/icons-material/Weekend";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import HistoryIcon from "@mui/icons-material/History";
import TodayIcon from "@mui/icons-material/Today";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ComputerIcon from "@mui/icons-material/Computer";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import { motion } from "framer-motion";
import { getAttendenceStatus } from "api/attendance";
import { useAuth } from "AuthContext";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { ArrowForward, Coffee, Notes, Place, Timer, TrendingUp } from "@mui/icons-material";
import SoftButton from "components/SoftButton";
import { FaCoffee } from "react-icons/fa";
import FlagLoader from "layouts/flagloader";

// Status color configuration based on your API
const STATUS_COLORS = {
  // Present statuses (Green)
  present: {
    color: "#10B981",
    bgColor: "#E8F5E9",
    icon: <CheckCircleIcon />,
    label: "Full",
  },
  full: {
    color: "#4CAF50",
    bgColor: "#E8F5E9",
    icon: <CheckCircleIcon />,
    label: "Full",
  },

  // Absent status (Red)
  absent: {
    color: "#EF4444",
    bgColor: "#FFEBEE",
    icon: <TimerOffIcon />,
    label: "Absent",
  },

  // Weekend/Off days (Orange)
  weekend_off: {
    color: "#F97316",
    bgColor: "#FFEDD5",
    icon: <BeachAccessIcon />,
    label: "Weekend",
  },
  holiday: {
    color: "#F97316",
    bgColor: "#FFEDD5",
    icon: <BeachAccessIcon />,
    label: "Holiday",
  },

  // Running/Live status (Blue)
  running: {
    color: "#3B82F6",
    bgColor: "#E0F2FE",
    icon: <PlayCircleIcon />,
    label: "Live",
  },

  already_logged_in: {
    color: "#3B82F6",
    bgColor: "#E0F2FE",
    icon: <PlayCircleIcon />,
    label: "Logged In",
  },

  half: {
    bg: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",
    bgLight: "linear-gradient(90deg, #E8F5E9 50%, #FFEBEE 50%)",
    text: "#525253",
    label: "Half Day",
    iconColor: "#525253",

    icon: () => (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",
          border: "1px solid #E5E7EB",
        }}
      />
    ),
  },

  // Default (Gray)
  default: {
    color: "#6B7280",
    bgColor: "#F3F4F6",
    icon: <InfoIcon />,
    label: "Unknown",
  },
};

const TimelineDetailsDrawer = ({
  open,
  onClose,
  timelineData,
  selectedDate,
  setSelectedDate,
  user,
}) => {
  const safeSelectedDate = useMemo(() => {
    if (!selectedDate) return null;

    // Agar string hai to convert karo
    if (typeof selectedDate === "string") {
      return new Date(selectedDate);
    }

    // Agar number/timestamp hai
    if (typeof selectedDate === "number") {
      return new Date(selectedDate);
    }

    // Agar Date object hai
    if (selectedDate instanceof Date) {
      return selectedDate;
    }

    // Agar object hai with date property
    if (selectedDate.date && selectedDate.date instanceof Date) {
      return selectedDate.date;
    }

    return null;
  }, [selectedDate]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user: authUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (open && selectedDate) {
      fetchAttendanceData();
      setSelectedTab(0);
    }
  }, [open, selectedDate]);

  const fetchAttendanceData = async () => {
    if (!safeSelectedDate) return;

    setLoading(true);
    setError(null);

    try {
      const dateStr = safeSelectedDate.toISOString().split("T")[0];
      const params = {
        date: dateStr,
        UserId: user?.id || authUser?.id,
      };
      const response = await getAttendenceStatus(params);
      setAttendanceData(response);
    } catch (err) {
      console.error("Error fetching attendance data:", err);
      setError("Failed to load attendance data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "--";
    try {
      // Handle both "HH:MM:SS" and "T" format
      let timeOnly = timeStr;
      if (timeStr.includes("T")) {
        timeOnly = timeStr.split("T")[1]?.split("+")[0];
      }

      const [hours, minutes] = timeOnly.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return "--";
    try {
      const parts = timeStr.split(":");
      return `${parts[0]}:${parts[1]}`;
    } catch {
      return timeStr;
    }
  };

  const formatMinutes = (minutes) => {
    if (!minutes && minutes !== 0) return "--";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getStatusConfig = (status, status_reason, attendance_status) => {
    // Priority: status_reason > status > attendance_status
    const statusKey = status;

    // If attendance_status is "running", show running status
    if (attendance_status === "running") {
      return STATUS_COLORS.running;
    }

    return STATUS_COLORS[statusKey] || STATUS_COLORS[status] || STATUS_COLORS.default;
  };

  const getDayTypeConfig = (dayType) => {
    switch (dayType) {
      case "weekend":
        return {
          color: "#F97316",
          bgColor: "#FFEDD5",
          icon: <BeachAccessIcon />,
          label: "Weekend",
        };
      case "holiday":
        return {
          color: "#FFD700",
          bgColor: "#FEF9C3",
          icon: <EventBusyIcon />,
          label: "Holiday",
        };
      case "leave":
        return {
          color: "#8B5CF6",
          bgColor: "#F3E5F5",
          icon: <BeachAccessIcon />,
          label: "Leave",
        };
      default:
        return {
          color: "#3B82F6",
          bgColor: "#E0F2FE",
          icon: <WorkIcon />,
          label: "Working Day",
        };
    }
  };

  const getSelectedDateTimeline = () => {
    if (!attendanceData || !attendanceData.timeline || !safeSelectedDate) return null;
    const dateStr = safeSelectedDate.toISOString().split("T")[0];
    return attendanceData.timeline.find((item) => item.date === dateStr) || null;
  };

  const getTimeDifference = (inTime, outTime) => {
    if (!inTime || !outTime) return 0;

    const inDate = new Date(`2000-01-01T${inTime}`);
    const outDate = new Date(`2000-01-01T${outTime}`);

    return Math.floor((outDate - inDate) / (1000 * 60)); // Return in minutes
  };

  const renderTodayAttendance = () => {
    if (!attendanceData || !safeSelectedDate) return null;

    const todayTimeline = getSelectedDateTimeline();
    const dayTypeConfig = getDayTypeConfig(todayTimeline?.type || attendanceData.day_type);
    const hasOvertime = todayTimeline?.overtime?.status && todayTimeline.overtime.status !== "not_applicable";

    const renderSessionCard = (title, subtitle, type, data) => {
      const isAttendance = type === 'attendance';
      const primaryColor = isAttendance ? "#3B82F6" : "#8B5CF6";
      const bgColor = isAttendance ? "#F0F9FF" : "#FAF5FF";
      const borderColor = isAttendance ? "#BAE6FD" : "#E9D5FF";

      const sessionData = isAttendance ? data : data?.overtime;
      const status = sessionData?.status || (isAttendance ? todayTimeline?.status : data?.overtime?.status);
      const isAbsent = status === 'absent';
      const isCompleted = status === 'completed' || status === 'full';
      const isRunning = status === 'running';

      // Get correct field names
      const loginIp = isAttendance ? sessionData?.attendance_login_ip : sessionData?.overtime_login_ip;
      const logoutIp = isAttendance ? sessionData?.attendance_logout_ip : sessionData?.overtime_logout_ip;
      const loginLocation = isAttendance ? sessionData?.attendance_login_location : sessionData?.overtime_login_location;
      const logoutLocation = isAttendance ? sessionData?.attendance_logout_location : sessionData?.overtime_logout_location;

      const getStatusIcon = () => {
        if (isAbsent) return <TimerOffIcon sx={{ fontSize: 18, color: "#EF4444" }} />;
        if (isCompleted) return <CheckCircleIcon sx={{ fontSize: 18, color: "#10B981" }} />;
        if (isRunning) return <PlayCircleIcon sx={{ fontSize: 18, color: "#3B82F6" }} />;
        return <PendingIcon sx={{ fontSize: 18, color: "#F59E0B" }} />;
      };

      const getStatusText = () => {
        if (isAbsent) return "Absent";
        if (isCompleted) return "Completed";
        if (isRunning) return "In Progress";
        return "Pending";
      };

      const getStatusColor = () => {
        if (isAbsent) return "#EF4444";
        if (isCompleted) return "#10B981";
        if (isRunning) return "#3B82F6";
        return "#F59E0B";
      };

      const isCoordinates = (str) => {
        if (!str) return false;
        const coordPattern = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;
        return coordPattern.test(str.trim());
      };

      const openGoogleMaps = (location) => {
        if (!location || !isCoordinates(location)) return;
        const cleanedLocation = location.replace(/\s+/g, '');
        window.open(`https://www.google.com/maps?q=${cleanedLocation}`, "_blank");
      };

      return (
        <Card sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: 2,
          height: "100%",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
        }}>
          {/* HEADER */}
          <Box sx={{
            bgcolor: bgColor,
            p: 1.5,
            borderBottom: `1px solid ${borderColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                bgcolor: primaryColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                {isAttendance ?
                  <AccessTimeIcon sx={{ fontSize: 18, color: "white" }} /> :
                  <TrendingUp sx={{ fontSize: 18, color: "white" }} />
                }
              </Box>
              <Box>
                <SoftTypography variant="subtitle1" fontWeight="bold" fontSize="0.95rem" color={primaryColor}>
                  {title}
                </SoftTypography>
                {/* <SoftTypography variant="caption" color="text.secondary" fontSize="0.95rem">
                {subtitle}
              </SoftTypography> */}
              </Box>
            </Box>

          </Box>

          <CardContent sx={{ p: 2 }}>
            {isAbsent ? (
              <Box textAlign="center" py={3}>
                <TimerOffIcon sx={{ fontSize: 36, color: "#EF4444", mb: 1 }} />
                <SoftTypography variant="body1" fontWeight="bold" fontSize="0.95rem" gutterBottom>
                  {title} - Absent
                </SoftTypography>
                <SoftTypography variant="body2" color="text.secondary" fontSize="0.95rem">
                  Scheduled: {formatTime(data?.shift_start)} – {formatTime(data?.shift_end)}
                </SoftTypography>
              </Box>
            ) : (
              <>
                {/* TIME CARDS */}
                <Grid container spacing={1.5} mb={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{
                      bgcolor: "#F0FDF4",
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid #10B98133`
                    }}>
                      <SoftTypography variant="body2" fontWeight="medium">
                        CHECK IN
                      </SoftTypography>
                      <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                        {sessionData?.in ? formatTime(sessionData.in) : "--:--"}
                      </SoftTypography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{
                      bgcolor: "#FEF2F2",
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid #EF444433`
                    }}>
                      <SoftTypography variant="body2" fontWeight="medium">
                        CHECK OUT
                      </SoftTypography>
                      <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                        {sessionData?.out ? formatTime(sessionData.out) : "--:--"}
                      </SoftTypography>
                    </Box>
                  </Grid>
                </Grid>

                {/* TOTAL HOURS */}
                <Box sx={{
                  bgcolor: bgColor,
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${borderColor}`,
                  mb: 2
                }}>
                  <Grid container alignItems="center">
                    <Grid item xs={8}>
                      <SoftTypography variant="body2" fontWeight="medium" >
                        Total {isAttendance ? "Worked" : "Overtime"} Hours -&nbsp;
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={4} textAlign="right">
                      <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                        {sessionData?.worked_minutes || sessionData?.overtime_worked_minutes
                          ? formatMinutes(sessionData.worked_minutes || sessionData.overtime_worked_minutes)
                          : "0h 0m"}
                      </SoftTypography>
                    </Grid>
                  </Grid>
                </Box>

                {/* BREAK TIME */}
                {(sessionData?.break_minutes || sessionData?.attendance_break_minutes || sessionData?.overtime_break_minutes) > 0 && (
                  <Box sx={{
                    bgcolor: "#FFFBEB",
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid #F59E0B33`,
                    mb: 2
                  }}>
                    <Grid container alignItems="center">
                      <Grid item xs={8}>
                        <SoftTypography variant="body2" fontWeight="medium">
                          Total Break Time - &nbsp;
                        </SoftTypography>
                      </Grid>
                      <Grid item xs={4} textAlign="right">
                        <SoftTypography variant="body2" color="#1F2937" fontWeight="medium" fontSize="0.8rem">
                          {formatMinutes(
                            sessionData.break_minutes ||
                            sessionData.attendance_break_minutes ||
                            sessionData.overtime_break_minutes ||
                            0
                          )}
                        </SoftTypography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* BREAKS LIST */}
                {sessionData?.breaks?.length > 0 && (
                  <Box mb={2}>
                    <SoftTypography variant="body2" fontWeight="bold" mb={1} color={primaryColor} fontSize="0.85rem">
                      Breaks
                    </SoftTypography>
                    <Stack spacing={1}>
                      {sessionData.breaks.map((b, i) => (
                        <Box key={i} sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1.5,
                          bgcolor: "#FFFBEB80",
                          borderRadius: 1,
                          border: "1px solid #FDE68A"
                        }}>
                          <Box>
                            <SoftTypography variant="body2" fontWeight="medium">
                              Break {i + 1}
                            </SoftTypography>
                            <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                              {formatTime(b.start)} – {b.end ? formatTime(b.end) : "Ongoing"}
                            </SoftTypography>
                          </Box>
                          <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                            {formatMinutes(b.minutes)}
                          </SoftTypography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* IP ADDRESSES */}
                <Box mb={2}>
                  <SoftTypography variant="body2" fontWeight="bold" mb={1} color={primaryColor} fontSize="0.85rem">
                    IP Addresses
                  </SoftTypography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{
                        p: 1.5,
                        bgcolor: "#F8FAFC",
                        borderRadius: 1,
                        border: "1px solid #E5E7EB"
                      }}>
                        <SoftTypography variant="body2" fontWeight="medium">
                          Login IP
                        </SoftTypography>
                        <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                          {loginIp || "N/A"}
                        </SoftTypography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{
                        p: 1.5,
                        bgcolor: "#F8FAFC",
                        borderRadius: 1,
                        border: "1px solid #E5E7EB"
                      }}>
                        <SoftTypography variant="body2" fontWeight="medium" >
                          Logout IP
                        </SoftTypography>
                        <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                          {logoutIp || "N/A"}
                        </SoftTypography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* LOCATION */}
                <Box>
                  <SoftTypography variant="body2" fontWeight="bold" mb={1} color={primaryColor} fontSize="0.85rem">
                    Location
                  </SoftTypography>

                  {loginLocation && (
                    <Box mb={1.5}>
                      <Box sx={{
                        p: 1.5,
                        bgcolor: "#F8FAFC",
                        borderRadius: 1,
                        border: "1px solid #E5E7EB",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}>
                        <Box flex={1}>
                          <SoftTypography variant="body2" fontWeight="medium">
                            Login Location
                          </SoftTypography>
                          <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                            {isCoordinates(loginLocation) ? "Coordinates" : loginLocation}
                          </SoftTypography>
                        </Box>
                        {isCoordinates(loginLocation) && (
                          <IconButton size="small" onClick={() => openGoogleMaps(loginLocation)} sx={{ p: 0.5, ml: 1 }}>
                            <OpenInNewIcon sx={{ fontSize: 16, color: primaryColor }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )}

                  {logoutLocation && (
                    <Box>
                      <Box sx={{
                        p: 1.5,
                        bgcolor: "#F8FAFC",
                        borderRadius: 1,
                        border: "1px solid #E5E7EB",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}>
                        <Box flex={1}>
                          <SoftTypography variant="body2" fontWeight="medium">
                            Logout Location
                          </SoftTypography>
                          <SoftTypography variant="body2" fontWeight="medium" color="#1F2937" fontSize="0.8rem">
                            {isCoordinates(logoutLocation) ? "Coordinates" : logoutLocation}
                          </SoftTypography>
                        </Box>
                        {isCoordinates(logoutLocation) && (
                          <IconButton size="small" onClick={() => openGoogleMaps(logoutLocation)} sx={{ p: 0.5, ml: 1 }}>
                            <OpenInNewIcon sx={{ fontSize: 16, color: primaryColor }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )}

                  {!loginLocation && !logoutLocation && (
                    <Box sx={{
                      p: 2,
                      bgcolor: "#F8FAFC",
                      borderRadius: 1,
                      border: "1px solid #E5E7EB",
                      textAlign: "center"
                    }}>
                      <Place sx={{ color: "#9CA3AF", fontSize: 24, mb: 1 }} />
                      <SoftTypography variant="caption" color="text.secondary" fontSize="0.95rem">
                        No location data
                      </SoftTypography>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      );
    };

    return (
      <Box sx={{ p: 2 }}>
        {/* HEADER */}
        {/* <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="h6" fontWeight="bold" fontSize="1.1rem">
            {safeSelectedDate.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric"
            })}
          </SoftTypography>

          <Chip
            icon={dayTypeConfig.icon}
            label={dayTypeConfig.label}
            size="small"
            sx={{
              bgcolor: dayTypeConfig.bgColor,
              color: dayTypeConfig.color,
              fontWeight: 600,
              fontSize: "0.75rem",
              height: 24
            }}
          />
        </Box>

        {user && (
          <Box display="flex" alignItems="center" gap={2} p={1.5} sx={{ bgcolor: "#F8FAFC", borderRadius: 1, border: "1px solid #E2E8F0" }}>
            <Avatar src={user.image} sx={{ width: 40, height: 40 }} />
            <Box flex={1}>
              <SoftTypography variant="body1" fontWeight="bold" fontSize="0.95rem">
                {user.name}
              </SoftTypography>
              <SoftTypography variant="caption" color="text.secondary" fontSize="0.75rem">
                {user?.shift ? `${user.shift.start_time} - ${user.shift.end_time}` : ""}
              </SoftTypography>
            </Box>
          </Box>
        )}
      </Box> */}

        {/* ALWAYS SHOW 2 COLUMNS */}
        <Box sx={{ p: 0 }}>
          <Grid
            container
            spacing={0}
            sx={{ width: "100%" }}
          >
            {/* ATTENDANCE */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                flexBasis: "50%",
                maxWidth: "50%",
                pr: 1,
              }}
            >
              {renderSessionCard(
                "Attendance",
                todayTimeline.status,
                "attendance",
                todayTimeline
              )}
            </Grid>

            {/* OVERTIME */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                flexBasis: "50%",
                maxWidth: "50%",
                pl: 1,
              }}
            >
              {
                renderSessionCard(
                  "Overtime",
                  "Extra Hours",
                  "overtime",
                  todayTimeline
                )
              }
            </Grid>
          </Grid>
        </Box>

      </Box>
    );
  };

  // const renderTimelineHistory = () => {
  //   if (!attendanceData || !attendanceData.timeline) return null;

  //   const todayStr = safeSelectedDate ? safeSelectedDate.toISOString().split("T")[0] : null;

  //   return (
  //     <Box sx={{ p: 2 }}>
  //       {/* Header Section */}
  //       <Box
  //         sx={{
  //           display: "flex",
  //           alignItems: "center",
  //           justifyContent: "space-between",
  //           mb: 3,
  //           px: 0.5,
  //         }}
  //       >
  //         <Box>
  //           <SoftTypography variant="h5" sx={{ mb: 0.25 }}>
  //             Attendance History
  //           </SoftTypography>
  //         </Box>
  //       </Box>

  //       {/* Column Headers - Adjusted widths */}
  //       <Box
  //         sx={{
  //           display: "grid",
  //           gridTemplateColumns: "0.8fr 1.4fr 1fr 0.9fr 0.9fr",
  //           gap: 2,
  //           mb: 2,
  //           px: 2,
  //           py: 1.5,
  //           bgcolor: "grey.50",
  //           borderRadius: "8px",
  //           border: "1px solid",
  //           borderColor: "divider",
  //           fontSize: "0.75rem",
  //         }}
  //       >
  //         <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
  //           DATE
  //         </SoftTypography>
  //         <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
  //           STATUS
  //         </SoftTypography>
  //         <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
  //           SHIFT HOURS
  //         </SoftTypography>
  //         <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
  //           CHECK-IN
  //         </SoftTypography>
  //         <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
  //           CHECK-OUT
  //         </SoftTypography>
  //       </Box>

  //       {/* Timeline Items */}
  //       <Stack spacing={1.5}>
  //         {attendanceData.timeline.map((day, index) => {
  //           const statusConfig = getStatusConfig(
  //             day.status,
  //             day.status_reason,
  //             day.attendance_status
  //           );
  //           const dayTypeConfig = getDayTypeConfig(day.type);
  //           const isSelectedDay = todayStr === day.date;
  //           const isToday = day.date === new Date().toISOString().split("T")[0];

  //           // Format times
  //           const checkInTime = day.in ? formatTimeDisplay(day.in) : "--";
  //           const checkOutTime = day.out ? formatTimeDisplay(day.out) : "--";
  //           const shiftStart = day.shift_start ? formatTimeDisplay(day.shift_start) : "--";
  //           const shiftEnd = day.shift_end ? formatTimeDisplay(day.shift_end) : "--";

  //           return (
  //             <motion.div
  //               key={day.date}
  //               initial={{ opacity: 0, y: 5 }}
  //               animate={{ opacity: 1, y: 0 }}
  //               transition={{ delay: index * 0.02 }}
  //             >
  //               <Card
  //                 sx={{
  //                   borderLeft: `3px solid ${statusConfig.color}`,
  //                   borderRadius: "10px",
  //                   overflow: "visible",
  //                   cursor: "pointer",
  //                   transition: "all 0.2s ease",
  //                   border: "1px solid",
  //                   borderColor: isSelectedDay ? statusConfig.color : "divider",
  //                   bgcolor: isSelectedDay ? `${statusConfig.color}08` : "background.paper",
  //                   boxShadow: isSelectedDay ? `0 4px 12px ${statusConfig.color}20` : "none",
  //                   "&:hover": {
  //                     transform: "translateY(-2px)",
  //                     boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
  //                     borderColor: statusConfig.color,
  //                   },
  //                   position: "relative",
  //                 }}
  //                 onClick={() => {
  //                   const newDate = new Date(day.date);
  //                   if (!isNaN(newDate)) {
  //                     setSelectedDate(newDate);
  //                     setSelectedTab(0);
  //                   }
  //                 }}
  //               >
  //                 <Box sx={{ p: 2 }}>
  //                   {/* Main Content Grid - Adjusted column sizes */}
  //                   <Box
  //                     sx={{
  //                       display: "grid",
  //                       gridTemplateColumns: "0.8fr 1.4fr 1fr 0.9fr 0.9fr",
  //                       gap: 2,
  //                       alignItems: "center",
  //                     }}
  //                   >
  //                     {/* Date Column */}
  //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
  //                       <Box
  //                         sx={{
  //                           width: 40,
  //                           height: 40,
  //                           minWidth: 40,
  //                           borderRadius: "10px",
  //                           display: "flex",
  //                           flexDirection: "column",
  //                           alignItems: "center",
  //                           justifyContent: "center",
  //                           bgcolor: `${statusConfig.color}12`,
  //                           color: statusConfig.color,
  //                           fontWeight: "bold",
  //                           border: `1.5px solid ${statusConfig.color}25`,
  //                           fontSize: "0.75rem",
  //                           flexShrink: 0,
  //                         }}
  //                       >
  //                         <SoftTypography
  //                           variant="body2"
  //                           fontWeight="bold"
  //                           lineHeight={1}
  //                           fontSize="0.875rem"
  //                         >
  //                           {new Date(day.date).getDate()}
  //                         </SoftTypography>
  //                         <SoftTypography variant="caption" lineHeight={1} fontSize="0.7rem">
  //                           {new Date(day.date)
  //                             .toLocaleDateString("en-US", { weekday: "short" })
  //                             .toUpperCase()}
  //                         </SoftTypography>
  //                       </Box>
  //                     </Box>

  //                     {/* Status Column - More space for notes */}
  //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
  //                       <Box
  //                         sx={{
  //                           width: 32,
  //                           height: 32,
  //                           minWidth: 32,
  //                           borderRadius: "8px",
  //                           display: "flex",
  //                           alignItems: "center",
  //                           justifyContent: "center",
  //                           bgcolor: `${statusConfig.color}15`,
  //                           color: statusConfig.color,
  //                           fontSize: "1rem",
  //                           flexShrink: 0,
  //                         }}
  //                       >
  //                         {React.cloneElement(statusConfig.icon, { fontSize: "small" })}
  //                       </Box>
  //                       <Box sx={{ minWidth: 0, flex: 1 }}>
  //                         <SoftTypography variant="h5" fontSize="0.85rem" sx={{ mb: 0.25 }} noWrap>
  //                           {statusConfig.label}
  //                         </SoftTypography>
  //                       </Box>
  //                     </Box>

  //                     {/* Shift Timings Column */}
  //                     <Box>
  //                       <Box
  //                         sx={{
  //                           display: "flex",
  //                           alignItems: "center",
  //                           justifyContent: "center",
  //                           bgcolor: "grey.50",
  //                           p: 1.2,
  //                           borderRadius: "8px",
  //                           border: "1px solid",
  //                           borderColor: "divider",
  //                           fontSize: "0.85rem",
  //                           minWidth: "fit-content",
  //                         }}
  //                       >
  //                         <SoftTypography
  //                           variant="body2"
  //                           fontWeight="600"
  //                           fontSize="0.85rem"
  //                           sx={{
  //                             color: shiftStart !== "--" ? "text.primary" : "text.disabled",
  //                             minWidth: 40,
  //                             textAlign: "center",
  //                           }}
  //                         >
  //                           {shiftStart}
  //                         </SoftTypography>
  //                         <ArrowForward
  //                           sx={{
  //                             fontSize: 16,
  //                             color: "text.secondary",
  //                             mx: 1,
  //                             opacity: 0.7,
  //                           }}
  //                         />
  //                         <SoftTypography
  //                           variant="body2"
  //                           fontWeight="600"
  //                           fontSize="0.85rem"
  //                           sx={{
  //                             color: shiftEnd !== "--" ? "text.primary" : "text.disabled",
  //                             minWidth: 40,
  //                             textAlign: "center",
  //                           }}
  //                         >
  //                           {shiftEnd}
  //                         </SoftTypography>
  //                       </Box>
  //                       {day.shift_name && (
  //                         <SoftTypography
  //                           variant="caption"
  //                           color="text.secondary"
  //                           fontSize="0.7rem"
  //                           textAlign="center"
  //                           display="block"
  //                           mt={0.5}
  //                         >
  //                           {day.shift_name}
  //                         </SoftTypography>
  //                       )}
  //                     </Box>

  //                     {/* Check-in Column */}
  //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
  //                       <Box
  //                         sx={{
  //                           width: 36,
  //                           height: 36,
  //                           minWidth: 36,
  //                           borderRadius: "10px",
  //                           display: "flex",
  //                           alignItems: "center",
  //                           justifyContent: "center",
  //                           bgcolor: checkInTime !== "--" ? "#10b98115" : "grey.100",
  //                           border: `2px solid ${checkInTime !== "--" ? "#10b981" : "transparent"}`,
  //                           color: checkInTime !== "--" ? "#10b981" : "text.disabled",
  //                           fontSize: "1rem",
  //                           flexShrink: 0,
  //                         }}
  //                       >
  //                         <LoginIcon fontSize="small" />
  //                       </Box>
  //                       <Box>
  //                         <SoftTypography
  //                           variant="body2"
  //                           fontWeight="bold"
  //                           fontSize="0.9rem"
  //                           sx={{
  //                             color: checkInTime !== "--" ? "#10b981" : "text.disabled",
  //                           }}
  //                         >
  //                           {checkInTime}
  //                         </SoftTypography>
  //                         {day.in_late_minutes > 0 && (
  //                           <SoftTypography
  //                             variant="caption"
  //                             color="error.main"
  //                             fontSize="0.7rem"
  //                             fontWeight="bold"
  //                             display="flex"
  //                             alignItems="center"
  //                             gap={0.5}
  //                           >
  //                             <AccessTimeIcon sx={{ fontSize: 10 }} />+{day.in_late_minutes}m late
  //                           </SoftTypography>
  //                         )}
  //                       </Box>
  //                     </Box>

  //                     {/* Check-out Column */}
  //                     <Box sx={{ display: "flex", alignItems:"center", gap: 1.5 }}>
  //                       <Box
  //                         sx={{
  //                           width: 36,
  //                           height: 36,
  //                           minWidth: 36,
  //                           borderRadius: "10px",
  //                           display: "flex",
  //                           alignItems: "center",
  //                           justifyContent: "center",
  //                           bgcolor: checkOutTime !== "--" ? "#ef444415" : "grey.100",
  //                           border: `2px solid ${
  //                             checkOutTime !== "--" ? "#ef4444" : "transparent"
  //                           }`,
  //                           color: checkOutTime !== "--" ? "#ef4444" : "text.disabled",
  //                           fontSize: "1rem",
  //                           flexShrink: 0,
  //                         }}
  //                       >
  //                         <LogoutIcon fontSize="small" />
  //                       </Box>
  //                       <Box>
  //                         <SoftTypography
  //                           variant="body2"
  //                           fontWeight="bold"
  //                           fontSize="0.9rem"
  //                           sx={{
  //                             color: checkOutTime !== "--" ? "#ef4444" : "text.disabled",
  //                           }}
  //                         >
  //                           {checkOutTime}
  //                         </SoftTypography>
  //                         {day.out_early_minutes > 0 && (
  //                           <SoftTypography
  //                             variant="caption"
  //                             color="warning.main"
  //                             fontSize="0.7rem"
  //                             fontWeight="bold"
  //                             display="flex"
  //                             alignItems="center"
  //                             gap={0.5}
  //                           >
  //                             <AccessTimeIcon sx={{ fontSize: 10 }} />-{day.out_early_minutes}m
  //                             early
  //                           </SoftTypography>
  //                         )}
  //                       </Box>
  //                     </Box>
  //                   </Box>

  //                   {/* Additional Info - Expanded view for selected day */}
  //                   {isSelectedDay && (
  //                     <motion.div
  //                       initial={{ opacity: 0, height: 0 }}
  //                       animate={{ opacity: 1, height: "auto" }}
  //                       transition={{ duration: 0.3 }}
  //                     >
  //                       <Box
  //                         sx={{
  //                           mt: 2,
  //                           pt: 2,
  //                           borderTop: "1px solid",
  //                           borderColor: "divider",
  //                           fontSize: "0.8rem",
  //                         }}
  //                       >
  //                         <Grid container spacing={2}>
  //                           <Grid item xs={4}>
  //                             <Box
  //                               sx={{
  //                                 display: "flex",
  //                                 justifyContent: "space-between",
  //                                 alignItems: "center",
  //                                 p: 1.5,
  //                                 bgcolor: "grey.50",
  //                                 borderRadius: "8px",
  //                               }}
  //                             >
  //                               <SoftTypography
  //                                 variant="caption"
  //                                 color="text.secondary"
  //                                 fontWeight="medium"
  //                               >
  //                                 Total Hours:&nbsp;
  //                               </SoftTypography>
  //                               <SoftTypography variant="caption">
  //                                 {day.worked_minutes ? day.worked_minutes : "--"}m
  //                               </SoftTypography>
  //                             </Box>
  //                           </Grid>
  //                           <Grid item xs={4}>
  //                             <Box
  //                               sx={{
  //                                 display: "flex",
  //                                 justifyContent: "space-between",
  //                                 alignItems: "center",
  //                                 p: 1.5,
  //                                 bgcolor: "grey.50",
  //                                 borderRadius: "8px",
  //                               }}
  //                             >
  //                               <SoftTypography
  //                                 variant="caption"
  //                                 color="text.secondary"
  //                                 fontWeight="medium"
  //                               >
  //                                 Breaks:&nbsp;
  //                               </SoftTypography>
  //                               <SoftTypography variant="body2" fontWeight="medium">
  //                                 {day.attendance_break_minutes
  //                                   ? day.attendance_break_minutes
  //                                   : "--"}
  //                                 m
  //                               </SoftTypography>
  //                             </Box>
  //                           </Grid>
  //                           {day.notes && (
  //                             <Grid item xs={12}>
  //                               <Box
  //                                 sx={{
  //                                   display: "flex",
  //                                   gap: 1.5,
  //                                   p: 1.5,
  //                                   bgcolor: "grey.50",
  //                                   borderRadius: "8px",
  //                                   alignItems: "flex-start",
  //                                 }}
  //                               >
  //                                 <Notes
  //                                   sx={{
  //                                     fontSize: 16,
  //                                     color: "text.secondary",
  //                                     mt: 0.2,
  //                                     flexShrink: 0,
  //                                   }}
  //                                 />
  //                                 <SoftTypography
  //                                   variant="caption"
  //                                   color="text.secondary"
  //                                   fontSize="0.8rem"
  //                                 >
  //                                   {day.notes}
  //                                 </SoftTypography>
  //                               </Box>
  //                             </Grid>
  //                           )}
  //                         </Grid>
  //                       </Box>
  //                     </motion.div>
  //                   )}
  //                 </Box>
  //               </Card>
  //             </motion.div>
  //           );
  //         })}
  //       </Stack>

  //       {/* Empty State */}
  //       {attendanceData.timeline.length === 0 && (
  //         <Box
  //           sx={{
  //             display: "flex",
  //             flexDirection: "column",
  //             alignItems: "center",
  //             justifyContent: "center",
  //             py: 8,
  //             textAlign: "center",
  //             bgcolor: "grey.50",
  //             borderRadius: "12px",
  //             border: "1px dashed",
  //             borderColor: "divider",
  //             mt: 2,
  //           }}
  //         >
  //           <Box
  //             sx={{
  //               width: 64,
  //               height: 64,
  //               borderRadius: "50%",
  //               display: "flex",
  //               alignItems: "center",
  //               justifyContent: "center",
  //               bgcolor: "background.paper",
  //               mb: 2,
  //               border: "1px solid",
  //               borderColor: "divider",
  //             }}
  //           >
  //             <HistoryIcon sx={{ fontSize: 32, color: "text.secondary" }} />
  //           </Box>
  //           <SoftTypography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
  //             No attendance records found
  //           </SoftTypography>
  //           <SoftTypography variant="caption" color="text.secondary" maxWidth="300px">
  //             There are no attendance records for the selected date range. Try selecting a different
  //             period.
  //           </SoftTypography>
  //         </Box>
  //       )}
  //     </Box>
  //   );
  // };


  const renderTimelineHistory = () => {
    if (!attendanceData || !attendanceData.timeline) return null;

    const todayStr = safeSelectedDate ? safeSelectedDate.toISOString().split("T")[0] : null;

    // Calculate totals
    let totalAttendanceMinutes = 0;
    let totalAttendanceBreakMinutes = 0;
    let totalOvertimeMinutes = 0;
    let totalOvertimeBreakMinutes = 0;

    attendanceData.timeline.forEach(day => {
      // Attendance totals
      if (day.worked_minutes) {
        totalAttendanceMinutes += day.worked_minutes;
      }
      if (day.attendance_break_minutes) {
        totalAttendanceBreakMinutes += day.attendance_break_minutes;
      }

      // Overtime totals
      if (day.overtime && day.overtime.overtime_worked_minutes) {
        totalOvertimeMinutes += day.overtime.overtime_worked_minutes;
      }
      if (day.overtime && day.overtime.overtime_break_minutes) {
        totalOvertimeBreakMinutes += day.overtime.overtime_break_minutes;
      }
    });

    return (
      <Box sx={{ p: 2 }}>
        {/* Column Headers - Adjusted widths */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "0.8fr 1.4fr 1fr 0.9fr 0.9fr",
            gap: 2,
            mb: 2,
            px: 2,
            py: 1.5,
            bgcolor: "grey.50",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            fontSize: "0.75rem",
          }}
        >
          <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
            DATE
          </SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
            STATUS
          </SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
            SHIFT HOURS
          </SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
            CHECK-IN
          </SoftTypography>
          <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
            CHECK-OUT
          </SoftTypography>
        </Box>

        {/* Timeline Items */}
        <Stack spacing={1.5}>
          {attendanceData.timeline.map((day, index) => {
            const statusConfig = getStatusConfig(
              day.status,
              day.status_reason,
              day.attendance_status
            );
            const dayTypeConfig = getDayTypeConfig(day.type);
            const isSelectedDay = todayStr === day.date;
            const isToday = day.date === new Date().toISOString().split("T")[0];

            // Format times
            const checkInTime = day.in ? formatTimeDisplay(day.in) : "--";
            const checkOutTime = day.out ? formatTimeDisplay(day.out) : "--";
            const shiftStart = day.shift_start ? formatTimeDisplay(day.shift_start) : "--";
            const shiftEnd = day.shift_end ? formatTimeDisplay(day.shift_end) : "--";

            // Calculate day totals
            const dayAttendanceMinutes = day.worked_minutes || 0;
            const dayAttendanceBreakMinutes = day.attendance_break_minutes || 0;
            const dayOvertimeMinutes = day.overtime?.overtime_worked_minutes || 0;
            const dayOvertimeBreakMinutes = day.overtime?.overtime_break_minutes || 0;

            return (
              <motion.div
                key={day.date}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02 }}
              >
                <Card
                  sx={{
                    borderLeft: `3px solid ${statusConfig.color}`,
                    borderRadius: "10px",
                    overflow: "visible",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    border: "1px solid",
                    borderColor: isSelectedDay ? statusConfig.color : "divider",
                    bgcolor: isSelectedDay ? `${statusConfig.color}08` : "background.paper",
                    boxShadow: isSelectedDay ? `0 4px 12px ${statusConfig.color}20` : "none",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
                      borderColor: statusConfig.color,
                    },
                    position: "relative",
                  }}
                  onClick={() => {
                    const newDate = new Date(day.date);
                    if (!isNaN(newDate)) {
                      setSelectedDate(newDate);
                      setSelectedTab(0);
                    }
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    {/* Main Content Grid - Adjusted column sizes */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "0.8fr 1.4fr 1fr 0.9fr 0.9fr",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      {/* Date Column */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            borderRadius: "10px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: `${statusConfig.color}12`,
                            color: statusConfig.color,
                            fontWeight: "bold",
                            border: `1.5px solid ${statusConfig.color}25`,
                            fontSize: "0.75rem",
                            flexShrink: 0,
                          }}
                        >
                          <SoftTypography
                            variant="body2"
                            fontWeight="bold"
                            lineHeight={1}
                            fontSize="0.875rem"
                          >
                            {new Date(day.date).getDate()}
                          </SoftTypography>
                          <SoftTypography variant="caption" lineHeight={1} fontSize="0.7rem">
                            {new Date(day.date)
                              .toLocaleDateString("en-US", { weekday: "short" })
                              .toUpperCase()}
                          </SoftTypography>
                        </Box>
                      </Box>

                      {/* Status Column - More space for notes */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: `${statusConfig.color}15`,
                            color: statusConfig.color,
                            fontSize: "1rem",
                            flexShrink: 0,
                          }}
                        >
                          {typeof statusConfig.icon === "function" ? (
                            <statusConfig.icon />
                          ) : React.isValidElement(statusConfig.icon) ? (
                            React.cloneElement(statusConfig.icon, { fontSize: "medium" })
                          ) : null}

                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <SoftTypography variant="h5" fontSize="0.85rem" sx={{ mb: 0.25 }} noWrap>
                            {statusConfig.label}
                          </SoftTypography>
                        </Box>
                      </Box>

                      {/* Shift Timings Column */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "grey.50",
                            p: 1.2,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: "divider",
                            fontSize: "0.85rem",
                            minWidth: "fit-content",
                          }}
                        >
                          <SoftTypography
                            variant="body2"
                            fontWeight="600"
                            fontSize="0.85rem"
                            sx={{
                              color: shiftStart !== "--" ? "text.primary" : "text.disabled",
                              minWidth: 40,
                              textAlign: "center",
                            }}
                          >
                            {shiftStart}
                          </SoftTypography>
                          <ArrowForward
                            sx={{
                              fontSize: 16,
                              color: "text.secondary",
                              mx: 1,
                              opacity: 0.7,
                            }}
                          />
                          <SoftTypography
                            variant="body2"
                            fontWeight="600"
                            fontSize="0.85rem"
                            sx={{
                              color: shiftEnd !== "--" ? "text.primary" : "text.disabled",
                              minWidth: 40,
                              textAlign: "center",
                            }}
                          >
                            {shiftEnd}
                          </SoftTypography>
                        </Box>
                        {day.shift_name && (
                          <SoftTypography
                            variant="caption"
                            color="text.secondary"
                            fontSize="0.7rem"
                            textAlign="center"
                            display="block"
                            mt={0.5}
                          >
                            {day.shift_name}
                          </SoftTypography>
                        )}
                      </Box>

                      {/* Check-in Column */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: checkInTime !== "--" ? "#10b98115" : "grey.100",
                            border: `2px solid ${checkInTime !== "--" ? "#10b981" : "transparent"}`,
                            color: checkInTime !== "--" ? "#10b981" : "text.disabled",
                            fontSize: "1rem",
                            flexShrink: 0,
                          }}
                        >
                          <LoginIcon fontSize="small" />
                        </Box>
                        <Box>
                          <SoftTypography
                            variant="body2"
                            fontWeight="bold"
                            fontSize="0.9rem"
                            sx={{
                              color: checkInTime !== "--" ? "#10b981" : "text.disabled",
                            }}
                          >
                            {checkInTime}
                          </SoftTypography>
                          {day.in_late_minutes > 0 && (
                            <SoftTypography
                              variant="caption"
                              color="error.main"
                              fontSize="0.7rem"
                              fontWeight="bold"
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                            >
                              <AccessTimeIcon sx={{ fontSize: 10 }} />+{day.in_late_minutes}m late
                            </SoftTypography>
                          )}
                        </Box>
                      </Box>

                      {/* Check-out Column */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: checkOutTime !== "--" ? "#ef444415" : "grey.100",
                            border: `2px solid ${checkOutTime !== "--" ? "#ef4444" : "transparent"
                              }`,
                            color: checkOutTime !== "--" ? "#ef4444" : "text.disabled",
                            fontSize: "1rem",
                            flexShrink: 0,
                          }}
                        >
                          <LogoutIcon fontSize="small" />
                        </Box>
                        <Box>
                          <SoftTypography
                            variant="body2"
                            fontWeight="bold"
                            fontSize="0.9rem"
                            sx={{
                              color: checkOutTime !== "--" ? "#ef4444" : "text.disabled",
                            }}
                          >
                            {checkOutTime}
                          </SoftTypography>
                          {day.out_early_minutes > 0 && (
                            <SoftTypography
                              variant="caption"
                              color="warning.main"
                              fontSize="0.7rem"
                              fontWeight="bold"
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                            >
                              <AccessTimeIcon sx={{ fontSize: 10 }} />-{day.out_early_minutes}m
                              early
                            </SoftTypography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Additional Info - Expanded view for selected day */}
                    {isSelectedDay && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box
                          sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            fontSize: "0.8rem",
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  p: 1.5,
                                  bgcolor: "#F0F9FF",
                                  borderRadius: "8px",
                                  border: "1px solid #BAE6FD",
                                }}
                              >
                                <SoftTypography
                                  variant="body2"
                                  color="#3B82F6"
                                  fontWeight="medium"
                                  sx={{ mb: 0.5 }}
                                >
                                  Worked Hours
                                </SoftTypography>
                                <SoftTypography variant="body2" color="#1E40AF">
                                  {formatMinutes(dayAttendanceMinutes)}
                                </SoftTypography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  p: 1.5,
                                  bgcolor: "#F0FDF4",
                                  borderRadius: "8px",
                                  border: "1px solid #A7F3D0",
                                }}
                              >
                                <SoftTypography
                                  variant="body2"
                                  color="#10B981"
                                  fontWeight="medium"
                                  sx={{ mb: 0.5 }}
                                >
                                  Breaks
                                </SoftTypography>
                                <SoftTypography variant="body2" color="#047857">
                                  {formatMinutes(dayAttendanceBreakMinutes)}
                                </SoftTypography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  p: 1.5,
                                  bgcolor: "#FAF5FF",
                                  borderRadius: "8px",
                                  border: "1px solid #DDD6FE",
                                }}
                              >
                                <SoftTypography
                                  variant="body2"
                                  color="#8B5CF6"
                                  fontWeight="medium"
                                  sx={{ mb: 0.5 }}
                                >
                                  Overtime
                                </SoftTypography>
                                <SoftTypography variant="body2" color="#6D28D9">
                                  {formatMinutes(dayOvertimeMinutes)}
                                </SoftTypography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  p: 1.5,
                                  bgcolor: "#FFFBEB",
                                  borderRadius: "8px",
                                  border: "1px solid #FDE68A",
                                }}
                              >
                                <SoftTypography
                                  variant="body2"
                                  color="#F59E0B"
                                  fontWeight="medium"
                                  sx={{ mb: 0.5 }}
                                >
                                  OT Breaks
                                </SoftTypography>
                                <SoftTypography variant="body2" color="#92400E">
                                  {formatMinutes(dayOvertimeBreakMinutes)}
                                </SoftTypography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </motion.div>
                    )}
                  </Box>
                </Card>
              </motion.div>
            );
          })}
        </Stack>

        {/* Empty State */}
        {attendanceData.timeline.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              textAlign: "center",
              bgcolor: "grey.50",
              borderRadius: "12px",
              border: "1px dashed",
              borderColor: "divider",
              mt: 2,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                mb: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <HistoryIcon sx={{ fontSize: 32, color: "text.secondary" }} />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderLoading = () => (
    <Box
      sx={{
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
      }}
    >
      {/* <CircularProgress size={60} sx={{ mb: 3, color: "#3B82F6" }} /> */}
      <FlagLoader />
      {/* <SoftTypography variant="h5" color="dark" mb={1}>
        Loading Attendance Details
      </SoftTypography>
      <SoftTypography variant="body2" color="text" fontWeight="medium">
        Fetching data for {safeSelectedDate?.toLocaleDateString()}
      </SoftTypography> */}
    </Box>
  );

  const renderError = () => (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <ErrorIcon sx={{ fontSize: 64, color: "#EF4444", mb: 3 }} />
      <SoftTypography variant="h5" color="error" mb={2}>
        {error}
      </SoftTypography>
      <SoftTypography variant="body2" color="text" fontWeight="medium" mb={3}>
        Unable to load attendance data for this date.
      </SoftTypography>
      <Button
        variant="contained"
        onClick={fetchAttendanceData}
        startIcon={<RefreshIcon />}
        sx={{
          bgcolor: "#3B82F6",
          "&:hover": { bgcolor: "#2563EB" },
        }}
      >
        Retry
      </Button>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 700,
          maxWidth: "100vw",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          overflow: "hidden",
          boxShadow: "0px 0px 40px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: "linear-gradient(135deg, #1A73E8 0%, #4FACFE 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 4px 20px rgba(26,115,232,0.3)",
        }}
      >
        <Box>
          <SoftTypography variant="h5" color="white">
            Attendance Details
          </SoftTypography>
          <SoftTypography variant="body2" color="white" fontWeight="medium">
            {safeSelectedDate?.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </SoftTypography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={fetchAttendanceData}
            disabled={loading}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "#F8FAFC",
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": {
              fontWeight: 550,
              textTransform: "none",
              fontSize: "0.875rem",
              py: 1.5,
              minHeight: "auto",
            },
            "& .Mui-selected": {
              color: "#1A73E8 !important",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#1A73E8",
              height: 3,
            },
          }}
        >
          <Tab icon={<TodayIcon fontSize="small" />} iconPosition="start" label="Details" />
          <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
        </Tabs>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: "#F8FAFC",
        }}
      >
        {loading ? (
          renderLoading()
        ) : error ? (
          renderError()
        ) : (
          <>
            {selectedTab === 0 && renderTodayAttendance()}
            {selectedTab === 1 && renderTimelineHistory()}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default TimelineDetailsDrawer;